import { useState, useEffect } from 'react';
import axios from 'axios';
import { LuX } from 'react-icons/lu';
import { resolveMediaUrl, getAppBase, getApiUrl, authHeaders, getTenantStorageKey } from '../utils/styleHelpers.js';
import { uploadMediaFile, IOS_FILE_INPUT_STYLE } from '../utils/imageUploadHelper.js';
import MediaLibraryModal from './MediaLibraryModal.jsx';

const isVideoFile = (url) => {
    if (!url) return false;
    const cleanUrl = String(url).split('?')[0].split('#')[0].toLowerCase();
    return cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.ogg') || cleanUrl.endsWith('.mov') || cleanUrl.startsWith('data:video/');
};

export const MediaInput = ({ value, onChange, onPreview, placeholder, size = 'normal', allowedTypes = ['image', 'video', 'document'] }) => {
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isPreviewVideo, setIsPreviewVideo] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState(null);
    const [isLibraryOpen, setIsLibraryOpen] = useState(false);

    // Reset local object preview only when the parent value prop is updated to match the uploaded path
    useEffect(() => {
        if (value && value === uploadedUrl) {
            setPreviewUrl(null);
            setUploadedUrl(null);
        }
    }, [value, uploadedUrl]);

    const handleUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const isVid = (file.type || '').startsWith('video/');
        setIsPreviewVideo(isVid);

        let localBlobUrl = null;
        try {
            localBlobUrl = URL.createObjectURL(file);
            setPreviewUrl(localBlobUrl);
            if (onPreview) onPreview(localBlobUrl, isVid);
        } catch (err) {
            console.warn('Could not create preview blob:', err);
        }

        setUploading(true);
        try {
            const result = await uploadMediaFile(file);
            if (result && result.url) {
                setUploadedUrl(result.url);
                onChange({ target: { value: result.url } });
            }
        } catch (err) {
            console.error('Upload error in MediaInput:', err);
        } finally {
            setUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleSelectFromLibrary = (selectedUrl) => {
        if (!selectedUrl) return;
        setPreviewUrl(null);
        setUploadedUrl(null);
        onChange({ target: { value: selectedUrl } });
        if (onPreview) {
            onPreview(resolveMediaUrl(selectedUrl), isVideoFile(selectedUrl));
        }
    };

    const isSmall = size === 'small';
    const inputStyle = isSmall ? {
        flex: '1 1 90px',
        minWidth: '60px',
        padding: '3px 8px',
        margin: '0',
        border: '1px solid #cbd5e1',
        borderRadius: '4px',
        height: '28px',
        fontSize: '11px',
        background: '#ffffff',
        color: '#333333',
        boxSizing: 'border-box'
    } : {
        flex: '1 1 140px',
        minWidth: '0',
        padding: '8px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '0.9rem',
        boxSizing: 'border-box'
    };

    const actionButtonStyle = (bgColor, textColor, borderColor) => isSmall ? {
        flex: '0 0 auto',
        cursor: uploading ? 'wait' : 'pointer',
        background: bgColor,
        color: textColor,
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        border: `1px solid ${borderColor}`,
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '28px',
        boxSizing: 'border-box',
        margin: 0,
        gap: '4px'
    } : {
        flex: '0 0 auto',
        cursor: uploading ? 'wait' : 'pointer',
        background: bgColor,
        color: textColor,
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        border: `1px solid ${borderColor}`,
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        gap: '6px'
    };

    const isValVideo = isVideoFile(value);
    const showVideo = previewUrl ? isPreviewVideo : isValVideo;
    const mediaSrc = previewUrl || resolveMediaUrl(value);

    return (
        <div style={{ display: 'flex', gap: isSmall ? '5px' : '8px', alignItems: 'center', width: '100%', boxSizing: 'border-box', flexWrap: 'wrap' }}>
            {mediaSrc && (
                <div style={{
                    width: isSmall ? '28px' : '36px',
                    height: isSmall ? '28px' : '36px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f1f5f9',
                    flexShrink: 0,
                    position: 'relative'
                }}>
                    {showVideo ? (
                        <video src={mediaSrc} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <img src={mediaSrc} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Preview" />
                    )}
                    {uploading && (
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: isSmall ? '10px' : '14px'
                        }}>
                            ⏳
                        </div>
                    )}
                </div>
            )}
            <input value={value || ''} onChange={onChange} placeholder={placeholder || (isSmall ? "Media URL..." : "Media URL (Image/Video)...")} style={inputStyle} />
            
            {/* Clear / Remove Media button */}
            {value && (
                <button
                    type="button"
                    onClick={() => {
                        setPreviewUrl(null);
                        setUploadedUrl(null);
                        if (onChange) onChange({ target: { value: '' } });
                        if (onPreview) onPreview('', false);
                    }}
                    title="Remove / Clear media"
                    style={actionButtonStyle('#fee2e2', '#ef4444', '#fca5a5')}
                >
                    <LuX size={isSmall ? 12 : 14} />{isSmall ? '' : ' Clear'}
                </button>
            )}
            
            {/* Choose from existing uploads / library button */}
            <button
                type="button"
                onClick={() => setIsLibraryOpen(true)}
                title="Select from existing uploads folder"
                style={actionButtonStyle('#eff6ff', '#1d4ed8', '#bfdbfe')}
            >
                🖼️ {isSmall ? '' : 'Library'}
            </button>

            {/* Direct Upload button */}
            <label
                style={{
                    ...actionButtonStyle('#f1f5f9', '#334155', '#cbd5e1'),
                    position: 'relative',
                    overflow: 'hidden'
                }}
                title="Upload new file from device"
            >
                {uploading ? '⏳' : (isSmall ? '📁' : '📁 Upload')}
                <input type="file" accept="image/*,video/*,.ico,.pdf" onChange={handleUpload} style={IOS_FILE_INPUT_STYLE} disabled={uploading} />
            </label>

            {/* Media Library Modal */}
            <MediaLibraryModal
                isOpen={isLibraryOpen}
                onClose={() => setIsLibraryOpen(false)}
                onSelect={handleSelectFromLibrary}
                initialSelectedUrl={value}
                allowedTypes={allowedTypes}
            />
        </div>
    );
};

export default MediaInput;
